import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, SlidersHorizontal, X } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { HeroBackground } from "@/components/HeroBackground";
import { Button } from "@/components/ui/button";

import galleryHero from "@/assets/mckay/roof-replacement.webp.asset.json";
import galleryIntro from "@/assets/mckay/san-diego-roofing-residential-roofing-contractor-3.jpg.asset.json";

import g1 from "@/assets/gallery-new/6940e3a8ab25949211304117-2.webp.asset.json";
import g2 from "@/assets/gallery-new/6940e3a8b4f420921f7ca893-2.webp.asset.json";
import g3 from "@/assets/gallery-new/6940e3a997c0231b045981a5-2.webp.asset.json";
import g4 from "@/assets/gallery-new/6940e3a8b4f42026887ca890-2.webp.asset.json";
import g5 from "@/assets/gallery-new/6940e3a896e7535d5af6346b-2.webp.asset.json";
import g6 from "@/assets/gallery-new/6940e3a896e753e8e1f6346a-2.webp.asset.json";
import g7 from "@/assets/gallery-new/6940e3a997c0238f425981a9-2.webp.asset.json";
import g8 from "@/assets/gallery-new/6940e3a8ab25944693304115-2.webp.asset.json";
import g9 from "@/assets/gallery-new/6940e3a8ca729802452657d2-2.webp.asset.json";
import mkRoofRepair from "@/assets/mckay/roof-repair.webp.asset.json";

import b1 from "@/assets/blog/blog-1.jpg.asset.json";
import b2 from "@/assets/blog/blog-2.jpg.asset.json";
import b3 from "@/assets/blog/blog-3.jpg.asset.json";
import b4 from "@/assets/blog/blog-4.jpg.asset.json";
import b5 from "@/assets/blog/blog-5.png.asset.json";
import b6 from "@/assets/blog/blog-6.png.asset.json";
import bLeaks from "@/assets/blog/What_Causes_Roof_Leaks_in_San_Diego.jpg.asset.json";
import bHomeowners from "@/assets/blog/What_Homeowners_Need_to_Know.jpg.asset.json";
import bRepairVsReplace from "@/assets/blog/Roof_Repair_vs_Replacement.jpg.asset.json";
import bCanYouInstall from "@/assets/blog/canyouinstall.jpg.asset.json";
import bHowLong from "@/assets/blog/howlongdoes.jpg.asset.json";
import bRepairCost from "@/assets/blog/howmuchdoesroofrepair.jpg.asset.json";
import bRepairCost2026 from "@/assets/blog/howmuchdoesroofrepair_real2026.jpg.asset.json";
import bWhichIsBetter from "@/assets/blog/whichisbetter.jpg.asset.json";

const PAGE_URL = "https://mckay.steadyautogrowthdemo.com/gallery";

export const Route = createFileRoute("/gallery")({
  head: () => ({
    meta: [
      { title: "Roofing & Solar Project Gallery | McKay Southern California" },
      {
        name: "description",
        content:
          "Photo gallery of roofing and solar projects finished across Southern California by McKay Roofing & Solar - replacements, repairs, flat roof systems, panels and batteries.",
      },
      { name: "keywords", content: "Southern California roofing gallery, roof replacement photos, solar install gallery Southern California" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { name: "publisher", content: "McKay Roofing & Solar" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Roofing & Solar Project Gallery | McKay Southern California" },
      {
        property: "og:description",
        content: "A look at real roofing and solar work McKay has completed across Southern California.",
      },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Roofing & Solar Project Gallery | McKay Southern California" },
      { name: "twitter:description", content: "Replacements, repairs, flat roof systems, panels and batteries - job by job." },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
  }),
  component: GalleryPage,
});

type Category = "roof-replacement" | "roof-repair" | "flat-roof" | "solar" | "battery" | "commercial";

type Item = { src: string; label: string; category: Category };

const rawItems: Item[] = [
  { src: g1.url, label: "Solar install on a shingle roof", category: "solar" },
  { src: g2.url, label: "Coastal home solar array", category: "solar" },
  { src: g3.url, label: "Solar panels on a tile roof", category: "solar" },
  { src: g4.url, label: "Multi-section residential solar array", category: "solar" },
  { src: g5.url, label: "Completed solar install, Southern California home", category: "solar" },
  { src: g6.url, label: "Flat-roof solar array", category: "flat-roof" },
  { src: g7.url, label: "Roof replacement with fresh underlayment", category: "roof-replacement" },
  { src: g8.url, label: "Residential solar on shingles", category: "solar" },
  { src: g9.url, label: "Tudor re-roof with new sheathing", category: "roof-replacement" },
  { src: mkRoofRepair.url, label: "Roof repair in progress", category: "roof-repair" },
  { src: bLeaks.url, label: "Tracing a roof leak in Southern California", category: "roof-repair" },
  { src: bRepairCost.url, label: "Roof repair assessment", category: "roof-repair" },
  { src: bRepairCost2026.url, label: "Shingle repair close-up", category: "roof-repair" },
  { src: bRepairVsReplace.url, label: "Repair or replace - checking the deck", category: "roof-replacement" },
  { src: bHowLong.url, label: "Re-roof underway on a Southern California home", category: "roof-replacement" },
  { src: bHomeowners.url, label: "Walking a homeowner through the roof", category: "roof-replacement" },
  { src: b1.url, label: "Finished shingle roof", category: "roof-replacement" },
  { src: b2.url, label: "New roof and clean flashing details", category: "roof-replacement" },
  { src: b3.url, label: "Flat roof system after coating", category: "flat-roof" },
  { src: b4.url, label: "Crew work on a low-slope roof", category: "flat-roof" },
  { src: b5.url, label: "Solar array on a finished roof", category: "solar" },
  { src: b6.url, label: "Panels and inverter setup", category: "solar" },
  { src: bCanYouInstall.url, label: "Solar panels going on an existing roof", category: "solar" },
  { src: bWhichIsBetter.url, label: "Comparing solar system options", category: "solar" },
];

const items: Item[] = rawItems.filter(
  (item, index) => rawItems.findIndex((other) => other.src === item.src) === index,
);

const allFilters: { key: Category; label: string }[] = [
  { key: "roof-replacement", label: "Roof Replacement" },
  { key: "roof-repair", label: "Roof Repair" },
  { key: "flat-roof", label: "Flat Roofs" },
  { key: "solar", label: "Solar" },
  { key: "battery", label: "Batteries & EV" },
  { key: "commercial", label: "Commercial" },
];

const presentCategories = new Set(items.map((i) => i.category));
const filters = allFilters.filter((f) => presentCategories.has(f.key));

const PAGE_SIZE = 12;

function LockScroll({ active }: { active: boolean }) {
  useEffect(() => {
    if (!active) return;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    const prev = {
      htmlOverflow: document.documentElement.style.overflow,
      bodyOverflow: document.body.style.overflow,
      bodyPaddingRight: document.body.style.paddingRight,
    };
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
    return () => {
      document.documentElement.style.overflow = prev.htmlOverflow;
      document.body.style.overflow = prev.bodyOverflow;
      document.body.style.paddingRight = prev.bodyPaddingRight;
    };
  }, [active]);
  return null;
}

function GalleryPage() {
  const [selected, setSelected] = useState<Category[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [lightbox, setLightbox] = useState<{ open: boolean; src: string; alt: string }>({
    open: false,
    src: "",
    alt: "",
  });

  const toggleCategory = (key: Category) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const filtered = useMemo(
    () => (selected.length === 0 ? items : items.filter((i) => selected.includes(i.category))),
    [selected],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [selected]);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="relative overflow-hidden bg-gradient-hero text-primary-foreground">
          <HeroBackground src={galleryHero.url} alt="Completed roof replacement on a Southern California home" />
          <div className="relative mx-auto flex min-h-[85vh] max-w-5xl flex-col justify-center px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
            <h1 className="font-display text-4xl font-extrabold text-balance sm:text-5xl lg:text-6xl">Gallery</h1>
            <p className="mt-5 max-w-2xl text-lg text-primary-foreground/85">
              A closer look at the roofs and solar systems we have put on homes and buildings all over Southern California
              County - tear-offs, repairs, flat roof systems, panels and batteries.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="btn-lift btn-shine">
                <Link to="/contact">Get My Free Estimate</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="btn-shine border-white/50 bg-white/10 text-white hover:bg-white hover:text-primary"
              >
                <Link to="/recent-work">See recent work</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-background py-16 lg:py-20">
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 md:grid-cols-2 lg:px-8">
            <div>
              <h2 className="font-display text-3xl font-extrabold leading-tight text-primary md:text-4xl">
                Work you can actually look at before you hire us
              </h2>
              <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground">
                <p>
                  Every photo here is a job our own crews finished - no stock houses, no borrowed portfolios. Some are
                  full tear-offs down to bare decking. Others are a single flashing detail we fixed on a Tuesday
                  morning because a homeowner found a stain on the ceiling.
                </p>
                <p>
                  Use the filter to narrow things down to whatever you are dealing with. If you are weighing solar,
                  look at the panel and battery sets; if your roof is 20-plus years old, the replacement photos show
                  what the process actually looks like from the driveway.
                </p>
                <p>
                  Seeing something close to your own house? Send us a photo of your roof and we will tell you
                  straight whether it needs a repair, a coating, or a replacement.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="overflow-hidden rounded-3xl border border-border shadow-2xl">
                <img
                  src={galleryIntro.url}
                  alt="McKay roofing crew working on a residential roof in Southern California"
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <h2 className="font-display text-3xl font-extrabold text-primary md:text-4xl">Project photos</h2>
            <div className="relative">
              <button
                onClick={() => setFilterOpen((v) => !v)}
                aria-expanded={filterOpen}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-primary-foreground transition hover:bg-primary/90"
              >
                <SlidersHorizontal className="h-4 w-4" />
                Filter
                {selected.length > 0 && (
                  <span className="rounded-full bg-primary-foreground/20 px-2 py-0.5 text-[10px]">
                    {selected.length}
                  </span>
                )}
              </button>

              {filterOpen && (
                <div className="absolute right-0 z-30 mt-3 w-[min(22rem,80vw)] rounded-2xl border border-border bg-card p-4 shadow-2xl">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Categories</div>
                    <button
                      onClick={() => setSelected([])}
                      className="text-xs uppercase tracking-widest text-accent hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {filters.map((f) => {
                      const isActive = selected.includes(f.key);
                      return (
                        <button
                          key={f.key}
                          onClick={() => toggleCategory(f.key)}
                          aria-pressed={isActive}
                          className={[
                            "rounded-full border px-3.5 py-1.5 text-xs font-semibold transition",
                            isActive
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border bg-transparent text-muted-foreground hover:border-accent/60 hover:text-foreground",
                          ].join(" ")}
                        >
                          {f.label}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setFilterOpen(false)}
                    className="mt-5 w-full rounded-full bg-accent px-4 py-2 text-xs font-bold uppercase tracking-widest text-accent-foreground"
                  >
                    Show {filtered.length} {filtered.length === 1 ? "photo" : "photos"}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {paginated.map((item) => (
              <button
                key={item.src + item.label}
                onClick={() => setLightbox({ open: true, src: item.src, alt: item.label })}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card text-left"
              >
                <div className="aspect-[4/3] w-full overflow-hidden">
                  <img
                    src={item.src}
                    alt={item.label}
                    loading="lazy"
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent p-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/90">{item.label}</div>
                </div>
              </button>
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="mt-10 text-center text-sm text-muted-foreground">No projects in this category yet.</p>
          )}

          {totalPages > 1 && (
            <div className="mt-12 flex flex-wrap items-center justify-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                aria-label="Previous page"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border text-muted-foreground transition hover:border-accent/60 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  aria-current={n === currentPage ? "page" : undefined}
                  className={[
                    "inline-flex h-10 min-w-10 items-center justify-center rounded-full border px-3 text-sm font-semibold transition",
                    n === currentPage
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border text-muted-foreground hover:border-accent/60 hover:text-foreground",
                  ].join(" ")}
                >
                  {n}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                aria-label="Next page"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border text-muted-foreground transition hover:border-accent/60 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </section>

        <LockScroll active={lightbox.open} />
        {lightbox.open &&
          typeof document !== "undefined" &&
          createPortal(
            <div
              className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
              onClick={() => setLightbox({ open: false, src: "", alt: "" })}
            >
              <button
                aria-label="Close"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightbox({ open: false, src: "", alt: "" });
                }}
                className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
              >
                <X className="h-5 w-5" />
              </button>
              <img
                src={lightbox.src}
                alt={lightbox.alt}
                onClick={(e) => e.stopPropagation()}
                className="max-h-[90vh] max-w-[95vw] rounded-xl object-contain shadow-2xl"
              />
            </div>,
            document.body,
          )}

        <CTABlock />
      </main>
      <SiteFooter />
    </div>
  );
}
